import styled, { CSSObject } from '@emotion/styled';
import { alpha } from '@mui/material';

export const ConflictHeader = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    marginBottom: '0.25rem',
  }),
);

export const Subtitle = styled.span(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
}));

export const CardsGrid = styled.div(({ theme }) => ({
  display: 'grid',
  gap: '0.75rem',
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

interface ChoiceCardProps {
  $active: boolean;
}

export const ChoiceCard = styled.button<ChoiceCardProps>(({ theme, $active }) => ({
  position: 'relative',
  appearance: 'none',
  textAlign: 'left',
  font: 'inherit',
  cursor: 'pointer',
  background: $active ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
  border: `1px solid ${$active ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.14)}`,
  borderRadius: 12,
  padding: '1.25rem 1.25rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  transition: 'border-color 180ms ease, background 180ms ease, box-shadow 220ms ease',
  boxShadow: $active ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
  '&:hover': {
    borderColor: $active ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.28),
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

export const CardLabel = styled.span(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.primary.dark,
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  borderRadius: 8,
  padding: '4px 10px',
  alignSelf: 'flex-start',
}));

interface CardCheckProps {
  $active: boolean;
}

export const CardCheck = styled.span<CardCheckProps>(({ theme, $active }) => ({
  position: 'absolute',
  top: 14,
  right: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: $active ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.18),
  transition: 'color 180ms ease, transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1)',
  transform: $active ? 'scale(1)' : 'scale(0.85)',
  '& svg': {
    fontSize: 22,
  },
}));

export const FieldRow = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '2px',
  paddingBottom: '0.5rem',
  borderBottom: `1px dashed ${alpha(theme.palette.text.primary, 0.08)}`,
  '&:last-of-type': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
}));

export const FieldLabel = styled.span(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  letterSpacing: '0.01em',
}));

export const FieldValue = styled.span(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  wordBreak: 'break-word',
}));

export const ButtonsRow = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column-reverse',
  gap: '0.5rem',
  marginTop: '0.25rem',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
}));
